/**
 * ENTITLEMENT-LIFECYCLE-1 Phase 1 — the confirm-with-a-reason dialog.
 *
 * Split out of EntitlementsPage.tsx under docs/FILE_SIZE_POLICY.md. The reason
 * is required here for the same rule the router enforces at 422: a change to
 * what a firm has bought is not self-explaining six months later, and an audit
 * row that says only "super_admin revoked QoE" answers nothing.
 */
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { PendingAction } from '@/components/admin/entitlementTypes'
import { ROLE_OPTIONS, productLabel } from '@/components/admin/entitlementTypes'

interface EntitlementActionDialogProps {
  pending: PendingAction | null
  reason: string
  onReasonChange: (value: string) => void
  role: string
  onRoleChange: (value: string) => void
  suggested: string[]
  submitting: boolean
  onCancel: () => void
  onConfirm: () => void
}

export function EntitlementActionDialog({
  pending,
  reason,
  onReasonChange,
  role,
  onRoleChange,
  suggested,
  submitting,
  onCancel,
  onConfirm,
}: EntitlementActionDialogProps) {
  const label = pending ? productLabel(pending.product) : ''

  return (
    <Dialog open={!!pending} onOpenChange={open => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {pending?.action === 'grant' ? 'Grant' : 'Revoke'} {label}
            {pending ? ` — ${pending.accountId}` : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {pending?.action === 'revoke' ? (
            <p className="text-sm text-gray-600">
              Their users lose access to {label} immediately. The grant is kept
              and stamped, not deleted — the record of what this firm held, and when,
              stays intact.
            </p>
          ) : (
            <div>
              <Label htmlFor="grant-role">Role carried by this grant</Label>
              <Select value={role} onValueChange={val => onRoleChange(val ?? role)}>
                <SelectTrigger id="grant-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(r => (
                    <SelectItem key={r} value={r}>
                      {r.replace(/_/g, ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label htmlFor="action-reason">Reason *</Label>
            {/* Suggested, not enforced: an operator will meet a case none of the
                four describes, and a closed list would push them into the
                nearest lie. The field itself is required. */}
            <Input
              id="action-reason"
              value={reason}
              onChange={e => onReasonChange(e.target.value)}
              placeholder={suggested.join(', ') || 'e.g. paid-manual'}
              data-testid="action-reason"
            />
            {suggested.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {suggested.map(s => (
                  <Button
                    key={s}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onReasonChange(s)}
                  >
                    {s}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant={pending?.action === 'revoke' ? 'destructive' : 'default'}
            onClick={onConfirm}
            disabled={submitting || !reason.trim()}
            data-testid="action-confirm"
          >
            {submitting
              ? 'Saving…'
              : pending?.action === 'grant'
                ? 'Grant product'
                : 'Revoke product'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
